import Vue from 'vue';
import AblyService from '@/services/ably';
import {
  FAILED,
  SIGNED,
  isLocked,
  isRunning,
} from '@/data/digital-signature';
import {
  isWhatsappSendEnabled,
  sendRecipeWhatsapp,
  shareRecipeWhatsappWeb,
} from '@/helpers/digital-prescription';

const EVENT_NAME = 'medical-record';
const WHATSAPP_FALLBACK_REASONS = ['templateNotApproved', 'whatsappNotEnabled'];

export const requestedSignature = Vue.observable({ id: null });

export default {
  data() {
    return {
      signature: null,
      discardingSignature: false,
      pendingWhatsapp: null,
      sendingWhatsapp: false,
    };
  },
  watch: {
    signature(value) {
      this.handlePendingWhatsapp(value);
    },
  },
  mounted() {
    if (this.$options.digitalSignatureRealtime !== false) {
      this.subscribeDigitalSignature();
    }
  },
  beforeDestroy() {
    if (this.$options.digitalSignatureRealtime !== false) {
      this.unsubscribeDigitalSignature();
    }
  },
  computed: {
    isDigitalSignatureEnabled() {
      const settings = this.$store.getters.branchSettings;
      return !!(settings && settings.digitalSignature && settings.digitalSignature.enabled);
    },
    isSignatureLocked() {
      return isLocked(this.signature);
    },
    isSignatureRunning() {
      return isRunning(this.signature);
    },
    isSigned() {
      return !!this.signature && this.signature.status === SIGNED;
    },
    isWhatsappSendEnabled() {
      return isWhatsappSendEnabled();
    },
  },
  methods: {
    digitalSignatureChannel() {
      const { user } = this.$store.state.auth;
      const ably = AblyService.instance(user.id);
      return ably ? ably.realtime.channels.get(`${user.branch.id}-digital-signature`) : null;
    },
    subscribeDigitalSignature() {
      const channel = this.digitalSignatureChannel();
      if (channel) {
        channel.subscribe(EVENT_NAME, this.onDigitalSignatureMessage);
      }
    },
    unsubscribeDigitalSignature() {
      const channel = this.digitalSignatureChannel();
      if (channel) {
        channel.unsubscribe(EVENT_NAME, this.onDigitalSignatureMessage);
      }
    },
    onDigitalSignatureMessage({ data }) {
      if (!data) {
        return;
      }

      if (typeof this.syncDigitalSignature === 'function') {
        this.syncDigitalSignature(data);
      }

      if (!this.isOwnDigitalSignature(data)) {
        return;
      }

      const previous = this.signature;
      this.signature = this.mergeDigitalSignature(previous, data);
      this.notifyDigitalSignature(previous, data);
    },
    mergeDigitalSignature(previous, data) {
      return data.status ? { ...(previous || {}), ...data } : null;
    },
    notifyDigitalSignature(previous, data) {
      if (!isRunning(previous)) {
        return;
      }

      if (data.status === SIGNED) {
        this.$toast.show('Documento assinado digitalmente');
      } else if (data.status === FAILED) {
        this.$toast.show(data.error || 'Não foi possível concluir a assinatura digital', {
          type: 'error',
        });
      }
    },
    requestDigitalSignature(medicalRecordId, payload, onRequested = null) {
      this.$digitalSignature.open(async () => {
        const { data } = await this.$httpX
          .post(`/medical-records/${medicalRecordId}/signatures`, payload);
        this.pendingWhatsapp = payload && payload.whatsappCellphone
          ? { signatureId: data.id, cellphone: payload.whatsappCellphone }
          : null;
        requestedSignature.id = data.id;
        if (!this.signature || this.signature.id !== data.id) {
          this.signature = data;
        }
        this.$digitalSignature.close();
        this.$toast.show('Documento enviado para assinatura no Bird ID');
        if (onRequested) {
          onRequested(data);
        }
        this.handlePendingWhatsapp(this.signature);
      });
    },
    handlePendingWhatsapp(signature) {
      const pending = this.pendingWhatsapp;

      if (!pending) {
        return;
      }

      if (!signature || signature.id !== pending.signatureId || signature.status === FAILED) {
        if (!signature || signature.id === pending.signatureId) {
          this.pendingWhatsapp = null;
        }
        return;
      }

      if (signature.status !== SIGNED) {
        return;
      }

      this.pendingWhatsapp = null;
      const message = signature.message || null;

      if (message && message.status === 'sent') {
        this.$toast.success('Receita enviada pelo WhatsApp');
        return;
      }

      const reason = message ? message.reason : null;

      if (WHATSAPP_FALLBACK_REASONS.includes(reason)) {
        this.openDigitalSignatureWhatsappWeb(pending.cellphone);
        return;
      }

      if (reason === 'invalidPhone') {
        this.$toast.show(
          'Celular inválido. A receita foi assinada, mas não foi enviada pelo WhatsApp.',
          { type: 'error' },
        );
        return;
      }

      this.$toast.show(
        (message && message.error)
          || 'A receita foi assinada, mas não foi possível enviar pelo WhatsApp.',
        { type: 'error' },
      );
    },
    openDigitalSignatureWhatsappWeb(cellphone) {
      if (!this.signature) {
        return null;
      }

      return shareRecipeWhatsappWeb(this, {
        cellphone,
        shareRoute: `/medical-record-signatures/${this.signature.id}/share`,
        buildText: this.digitalSignatureWhatsappText,
      });
    },
    async sendDigitalSignatureWhatsapp(cellphone) {
      if (!this.signature || this.sendingWhatsapp) {
        return;
      }

      const route = `/medical-record-signatures/${this.signature.id}`;
      this.sendingWhatsapp = true;

      try {
        await sendRecipeWhatsapp(this, {
          cellphone,
          messageRoute: `${route}/message`,
          shareRoute: `${route}/share`,
          buildText: this.digitalSignatureWhatsappText,
          fallbackOnInvalidPhone: true,
        });
      } finally {
        this.sendingWhatsapp = false;
      }
    },
    digitalSignatureWhatsappText(documentUrl) {
      return documentUrl;
    },
    discardDigitalSignature(documentType) {
      if (!this.signature || this.discardingSignature) {
        return;
      }

      this.$discardSignature.open({
        documentType,
        discard: async () => {
          if (!this.signature) {
            return;
          }
          this.discardingSignature = true;
          try {
            await this.$httpX.delete(`/medical-record-signatures/${this.signature.id}`);
            this.signature = null;
            this.$toast.show('Assinatura descartada');
          } finally {
            this.discardingSignature = false;
          }
        },
      });
    },
  },
};
